import { useEffect } from "react"
import { FaUpload, FaCheckCircle, FaFlag } from "react-icons/fa"
import { FileText, Download, Calendar, FileType } from "lucide-react"
import {
  onboardingService,
  type DocumentToBeSignedDto,
} from "../../infrastructure/employeeService"
import { Button } from "@/modules/shared/ui/components/button"
import { toast } from "sonner"
import type {
  EmployeeFieldFlagDto,
  RequiredDocumentUploadDto,
} from "@/modules/employee/employee/dto/employeeDetails"

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"]

interface DocumentsUploadFormProps {
  requiredDocuments: string[]
  requiredDocumentUploads?: RequiredDocumentUploadDto[]
  documentsToBeSigned: DocumentToBeSignedDto[]
  onFileChange: (_field: unknown, _file: File | null) => void
  onValidityChange?: (isValid: boolean) => void
  onDocumentsChange?: (
    requiredDocs: Map<string, File>,
    signedDocs: Map<string, File>,
  ) => void
  requiredDocFiles: Map<string, File>
  signedDocFiles: Map<string, File>
  isReadOnly?: boolean
  fieldFlags?: EmployeeFieldFlagDto[]
}

export function DocumentsUploadForm({
  requiredDocuments,
  requiredDocumentUploads = [],
  documentsToBeSigned,
  onFileChange: _onFileChange,
  onValidityChange,
  onDocumentsChange,
  requiredDocFiles,
  signedDocFiles,
  isReadOnly = false,
  fieldFlags = [],
}: DocumentsUploadFormProps) {
  const sectionFlag = fieldFlags?.find(
    (f) => f.fieldName === "documents" && !f.resolvedAt,
  )
  const isSectionFlagged = !!sectionFlag
  const isFieldDisabled = isReadOnly && !isSectionFlagged
  useEffect(() => {
    const hasRequiredDocs =
      requiredDocuments.length === 0 ||
      requiredDocuments.every(
        (docType: string) =>
          requiredDocFiles.has(docType) ||
          requiredDocumentUploads.some(
            (u) => u.documentName === docType && u.submitted,
          ),
      )
    const hasSignedDocs =
      documentsToBeSigned.length === 0 ||
      documentsToBeSigned
        .filter((d: DocumentToBeSignedDto) => !d.hasSigned)
        .every((d: DocumentToBeSignedDto) => signedDocFiles.has(d.id))
    onValidityChange?.(hasRequiredDocs && hasSignedDocs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredDocFiles, signedDocFiles, requiredDocuments, documentsToBeSigned])

  const handleDownload = async (doc: DocumentToBeSignedDto) => {
    try {
      const downloadData = await onboardingService.getMyDocumentDownloadUrl(
        doc.id,
      )
      window.open(downloadData.downloadUrl, "_blank")
    } catch (_err) {
      toast.error("Failed to download document. Please try again.")
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const handleRequiredDocFileSelect = (
    documentType: string,
    file: File | null,
  ) => {
    const newMap = new Map(requiredDocFiles)
    if (!file) {
      newMap.delete(documentType)
      onDocumentsChange?.(newMap, signedDocFiles)
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must be less than 5MB")
      return
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error("Only PDF, DOC, DOCX allowed")
      return
    }

    newMap.set(documentType, file)
    onDocumentsChange?.(newMap, signedDocFiles)
  }

  const handleSignedDocFileSelect = (docId: string, file: File | null) => {
    const newMap = new Map(signedDocFiles)
    if (!file) {
      newMap.delete(docId)
      onDocumentsChange?.(requiredDocFiles, newMap)
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must be less than 5MB")
      return
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error("Only PDF, DOC, DOCX allowed")
      return
    }

    newMap.set(docId, file)
    onDocumentsChange?.(requiredDocFiles, newMap)
  }

  return (
    <div className="space-y-8">
      {isSectionFlagged && (
        <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
          <FaFlag className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium mb-1">This section has been rejected:</p>
            <p className="text-orange-700">{sectionFlag?.note}</p>
          </div>
        </div>
      )}
      {/* Required Documents Section */}
      {requiredDocuments.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">
              Required Documents
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Select the following documents (PDF/DOC/DOCX, max 5MB each). They
              will be uploaded when you submit the form.
            </p>
          </div>

          <div className="space-y-3">
            {requiredDocuments.map((docType: string) => {
              const selectedFile = requiredDocFiles.get(docType)
              const previousUpload = requiredDocumentUploads.find(
                (u) => u.documentName === docType && u.submitted,
              )
              const alreadyUploaded = !!previousUpload && !selectedFile
              const canReUpload = !previousUpload || isSectionFlagged
              return (
                <div
                  key={docType}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    alreadyUploaded
                      ? "border-green-200 bg-green-50"
                      : "border-border bg-muted"
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <FileText
                      className={`h-5 w-5 ${alreadyUploaded ? "text-green-600" : "text-muted-foreground"}`}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{docType}</p>
                      {selectedFile && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <FaCheckCircle className="text-green-600" />
                          {selectedFile.name} (
                          {formatFileSize(selectedFile.size)})
                        </p>
                      )}
                      {alreadyUploaded && previousUpload && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <FaCheckCircle className="text-green-600" />
                          Uploaded: {previousUpload.fileName} (
                          {formatFileSize(previousUpload.size)})
                        </p>
                      )}
                    </div>
                  </div>
                  {canReUpload && !isFieldDisabled && (
                    <>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) =>
                          handleRequiredDocFileSelect(
                            docType,
                            e.target.files?.[0] || null,
                          )
                        }
                        className="hidden"
                        id={`required-${docType}`}
                        disabled={isFieldDisabled}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          document
                            .getElementById(`required-${docType}`)
                            ?.click()
                        }
                        disabled={isFieldDisabled}
                      >
                        <FaUpload className="h-3 w-3 mr-2" />
                        {selectedFile
                          ? "Change"
                          : previousUpload
                            ? "Re-upload"
                            : "Select"}
                      </Button>
                    </>
                  )}
                  {alreadyUploaded && !canReUpload && (
                    <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
                      ✓ Uploaded
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Documents To Be Downloaded and Signed Section */}
      {documentsToBeSigned.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">
              Documents To Be Downloaded and Signed
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Download each document, sign it, then select the signed version.
              It will be uploaded when you submit the form.
            </p>
          </div>

          <div className="space-y-3">
            {documentsToBeSigned.map((doc: DocumentToBeSignedDto) => {
              const selectedFile = signedDocFiles.get(doc.id)
              return (
                <div
                  key={doc.id}
                  className="p-4 rounded-lg border border-border bg-card"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-blue-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {doc.fileName}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <FileType className="h-3 w-3" />
                            {formatFileSize(doc.size)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(doc.uploadedAt)}
                          </span>
                          {selectedFile && (
                            <span className="flex items-center gap-1 text-green-600">
                              <FaCheckCircle className="h-3 w-3" />
                              {selectedFile.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {doc.hasSigned && (
                        <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
                          {doc.approved ? "✓ Approved" : "Signed"}
                        </span>
                      )}
                      <Button
                        onClick={() => handleDownload(doc)}
                        variant="outline"
                        size="sm"
                      >
                        <Download className="h-3 w-3 mr-2" />
                        Download
                      </Button>
                      {(!doc.hasSigned || isSectionFlagged) &&
                        !isFieldDisabled && (
                          <>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) =>
                                handleSignedDocFileSelect(
                                  doc.id,
                                  e.target.files?.[0] || null,
                                )
                              }
                              className="hidden"
                              id={`signed-${doc.id}`}
                              disabled={isFieldDisabled}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                document
                                  .getElementById(`signed-${doc.id}`)
                                  ?.click()
                              }
                              disabled={isFieldDisabled}
                            >
                              <FaUpload className="h-3 w-3 mr-2" />
                              {selectedFile
                                ? "Change Signed"
                                : doc.hasSigned
                                  ? "Re-upload Signed"
                                  : "Select Signed"}
                            </Button>
                          </>
                        )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {requiredDocuments.length === 0 && documentsToBeSigned.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-1">No documents required</p>
          <p className="text-sm text-muted-foreground">
            You don't have any documents to upload at this time
          </p>
        </div>
      )}
    </div>
  )
}
